
import * as THREE from "../../libs/three.js/build/three.module.js";
import {CurbReveal} from "./CurbReveal.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";

export class CurbRevealTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;

		this.addEventListener("start_inserting_curb_reveal", e => {
			this.viewer.dispatchEvent({
				type: "cancel_insertions"
			});
		});

		this.toolScene = new THREE.Scene();
		this.toolScene.name = "scene_curb_reveal";
		this.light = new THREE.PointLight(0xffffff, 1.0);
		this.toolScene.add(this.light);

		this.viewer.inputHandler.registerInteractiveScene(this.toolScene);

		this.onRemove = e => {
			this.toolScene.remove(e.curbReveal);
			if (e.curbReveal._lineObjects) {
				for (let obj of e.curbReveal._lineObjects) {
					this.toolScene.remove(obj);
				}
				e.curbReveal._lineObjects = null;
			}
			if (e.curbReveal.metricsPanel) {
				e.curbReveal.metricsPanel.remove();
				e.curbReveal.metricsPanel = null;
			}
		};
		this.onAdd = e => this.toolScene.add(e.curbReveal);

		for (let cr of viewer.scene.curbReveals) {
			this.onAdd({curbReveal: cr});
		}

		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.perspective_overlay", this.render.bind(this));
		viewer.addEventListener("scene_changed", this.onSceneChange.bind(this));

		viewer.scene.addEventListener("curb_reveal_added", this.onAdd);
		viewer.scene.addEventListener("curb_reveal_removed", this.onRemove);
	}

	onSceneChange(e) {
		if (e.oldScene) {
			e.oldScene.removeEventListeners("curb_reveal_added", this.onAdd);
			e.oldScene.removeEventListeners("curb_reveal_removed", this.onRemove);
		}

		e.scene.addEventListener("curb_reveal_added", this.onAdd);
		e.scene.addEventListener("curb_reveal_removed", this.onRemove);
	}

	setScene(scene) {
		// No-op: CurbRevealTool manages its own toolScene
	}

	// 2-click UI: user draws a line along the curb, system creates a
	// perpendicular transect to auto-detect the edge and compute reveal.
	startInsertion() {
		let domElement = this.viewer.renderer.domElement;
		let canvasSize = this.viewer.renderer.getSize(new THREE.Vector2());

		let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("width", canvasSize.width);
		svg.setAttribute("height", canvasSize.height);
		svg.style.position = "absolute";
		svg.style.pointerEvents = "none";
		svg.style.top = "0";
		svg.style.left = "0";
		domElement.parentElement.appendChild(svg);

		let statusText = document.createElementNS("http://www.w3.org/2000/svg", "text");
		statusText.setAttribute("x", "20");
		statusText.setAttribute("y", "30");
		statusText.setAttribute("fill", "white");
		statusText.setAttribute("stroke", "black");
		statusText.setAttribute("stroke-width", "0.5");
		statusText.setAttribute("font-size", "16");
		statusText.setAttribute("font-family", "sans-serif");
		statusText.textContent = "Click START of curb line";
		svg.appendChild(statusText);

		// Curb line (green dashed)
		let curbLineSvg = document.createElementNS("http://www.w3.org/2000/svg", "line");
		curbLineSvg.setAttribute("stroke", "rgb(0, 204, 102)");
		curbLineSvg.setAttribute("stroke-width", "3");
		curbLineSvg.setAttribute("stroke-dasharray", "6, 4");
		curbLineSvg.style.display = "none";
		svg.appendChild(curbLineSvg);

		// Preview line (yellow dashed)
		let previewLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
		previewLine.setAttribute("stroke", "rgba(255,255,0,0.8)");
		previewLine.setAttribute("stroke-width", "2");
		previewLine.setAttribute("stroke-dasharray", "4, 4");
		previewLine.style.display = "none";
		svg.appendChild(previewLine);

		let vertexMarkers = document.createElementNS("http://www.w3.org/2000/svg", "g");
		svg.appendChild(vertexMarkers);

		let camera = this.viewer.scene.getActiveCamera();
		let curbReveal = new CurbReveal(camera);

		this.dispatchEvent({
			type: "start_inserting_curb_reveal",
			curbReveal: curbReveal
		});

		this.viewer.scene.addCurbReveal(curbReveal);

		let endpoints = [];

		let addVertexMarker = (x, y) => {
			let circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
			circle.setAttribute("cx", x);
			circle.setAttribute("cy", y);
			circle.setAttribute("r", "6");
			circle.setAttribute("fill", "rgb(0, 204, 102)");
			circle.setAttribute("stroke", "white");
			circle.setAttribute("stroke-width", "2");
			vertexMarkers.appendChild(circle);
		};

		let firstScreenPoint = null;

		let onMouseMove = (e) => {
			if (!firstScreenPoint) { return; }
			let rect = domElement.getBoundingClientRect();
			let x = e.clientX - rect.left;
			let y = e.clientY - rect.top;
			previewLine.setAttribute("x1", firstScreenPoint.x);
			previewLine.setAttribute("y1", firstScreenPoint.y);
			previewLine.setAttribute("x2", x);
			previewLine.setAttribute("y2", y);
			previewLine.style.display = "";
		};

		let finalize = () => {
			domElement.removeEventListener("mouseup", onMouseUp, true);
			domElement.removeEventListener("mousemove", onMouseMove, true);
			domElement.removeEventListener("contextmenu", onContextMenu, true);
			this.viewer.removeEventListener("cancel_insertions", cancel);
			svg.remove();
			this.viewer.inputHandler.enabled = true;
		};

		let cancel = () => {
			this.viewer.scene.removeCurbReveal(curbReveal);
			finalize();
		};

		let complete = () => {
			curbReveal.userLine.start = endpoints[0].worldPos;
			curbReveal.userLine.end = endpoints[1].worldPos;
			finalize();
			this.computeAutoReveal(curbReveal);
		};

		let onMouseUp = (e) => {
			if (e.button === 0) {
				let rect = domElement.getBoundingClientRect();
				let x = e.clientX - rect.left;
				let y = e.clientY - rect.top;

				let mouse = new THREE.Vector2(x, y);
				let cam = this.viewer.scene.getActiveCamera();
				let pointclouds = this.viewer.scene.pointclouds;
				let I = Utils.getMousePointCloudIntersection(
					mouse, cam, this.viewer, pointclouds
				);

				if (!I) { return; }

				let worldPos = I.location.clone();
				endpoints.push({ screenX: x, screenY: y, worldPos });
				addVertexMarker(x, y);

				if (endpoints.length === 1) {
					firstScreenPoint = { x, y };
					statusText.textContent = "Click END of curb line";
				} else if (endpoints.length === 2) {
					curbLineSvg.setAttribute("x1", endpoints[0].screenX);
					curbLineSvg.setAttribute("y1", endpoints[0].screenY);
					curbLineSvg.setAttribute("x2", endpoints[1].screenX);
					curbLineSvg.setAttribute("y2", endpoints[1].screenY);
					curbLineSvg.style.display = "";
					complete();
				}
			} else if (e.button === 2) {
				cancel();
			}
		};

		let onContextMenu = (e) => { e.preventDefault(); };

		this.viewer.addEventListener("cancel_insertions", cancel);
		domElement.addEventListener("mouseup", onMouseUp, true);
		domElement.addEventListener("mousemove", onMouseMove, true);
		domElement.addEventListener("contextmenu", onContextMenu, true);

		this.viewer.inputHandler.enabled = false;
		return curbReveal;
	}

	// Core algorithm: create a perpendicular transect from the user's curb
	// line, collect elevation profile, detect the curb edge, compute reveal.
	computeAutoReveal(curbReveal) {
		let userStart = curbReveal.userLine.start;
		let userEnd = curbReveal.userLine.end;

		// User's curb direction (2D, XY plane)
		let cdx = userEnd.x - userStart.x;
		let cdy = userEnd.y - userStart.y;
		let curbLength = Math.sqrt(cdx * cdx + cdy * cdy);

		if (curbLength < 0.3) {
			this.showWarningOverlay(curbReveal, "Curb line too short — draw a longer line along the curb");
			return;
		}

		// Normalized curb direction
		let curbDirX = cdx / curbLength;
		let curbDirY = cdy / curbLength;

		// Perpendicular direction (the transect direction, crossing the curb)
		let transectDirX = curbDirY;
		let transectDirY = -curbDirX;

		// Midpoint of user's line = center of perpendicular transect
		let midX = (userStart.x + userEnd.x) / 2;
		let midY = (userStart.y + userEnd.y) / 2;
		let midZ = (userStart.z + userEnd.z) / 2;

		// Virtual transect: 1.5m long, perpendicular to user's line
		let transectLength = 1.5;
		let transectHalf = transectLength / 2;
		let transectStart = new THREE.Vector3(
			midX - transectDirX * transectHalf,
			midY - transectDirY * transectHalf,
			midZ
		);
		let transectEnd = new THREE.Vector3(
			midX + transectDirX * transectHalf,
			midY + transectDirY * transectHalf,
			midZ
		);

		curbReveal.transectLine.start = transectStart;
		curbReveal.transectLine.end = transectEnd;

		// Swath width along the curb direction (use the user's line length,
		// capped at a reasonable range)
		let halfSwath = Math.min(curbLength / 2, 1.0);
		halfSwath = Math.max(halfSwath, 0.15); // at least 0.3m total

		// --- Stage 1: Collect points along the perpendicular transect ---
		let collectedPoints = [];
		let pointclouds = this.viewer.scene.pointclouds;

		// Pre-read viewer filter ranges
		let zRange = this.viewer.filterZRange;
		let hasZFilter = isFinite(zRange[0]) || isFinite(zRange[1]);
		let intensityRange = this.viewer.filterIntensityRange;
		let hasIntensityFilter = isFinite(intensityRange[0]) || isFinite(intensityRange[1]);
		let returnNumberRange = this.viewer.filterReturnNumberRange;
		let hasReturnNumberFilter = returnNumberRange &&
			(isFinite(returnNumberRange[0]) || isFinite(returnNumberRange[1]));
		let numberOfReturnsRange = this.viewer.filterNumberOfReturnsRange;
		let hasNumberOfReturnsFilter = numberOfReturnsRange &&
			(isFinite(numberOfReturnsRange[0]) || isFinite(numberOfReturnsRange[1]));
		let gpsTimeRange = this.viewer.filterGPSTimeRange;
		let hasGPSTimeFilter = gpsTimeRange &&
			(isFinite(gpsTimeRange[0]) || isFinite(gpsTimeRange[1]));
		let pointSourceIDRange = this.viewer.filterPointSourceIDRange;
		let hasPointSourceIDFilter = pointSourceIDRange &&
			(isFinite(pointSourceIDRange[0]) || isFinite(pointSourceIDRange[1]));

		for (let pointcloud of pointclouds) {
			if (!pointcloud.visible) { continue; }

			let nodes = pointcloud.visibleNodes;
			if (!nodes) { continue; }

			for (let node of nodes) {
				let geometry = node.geometryNode.geometry;
				if (!geometry) { continue; }

				let positionAttribute = geometry.attributes.position;
				if (!positionAttribute) { continue; }

				let nodeWorld = node.sceneNode.matrixWorld;
				let positions = positionAttribute.array;
				let numPoints = positionAttribute.count;

				let classificationAttr = geometry.attributes.classification;
				let classificationArray = classificationAttr ? classificationAttr.array : null;
				let intensityArray = hasIntensityFilter && geometry.attributes.intensity
					? geometry.attributes.intensity.array : null;
				let returnNumberArray = hasReturnNumberFilter && geometry.attributes.returnNumber
					? geometry.attributes.returnNumber.array : null;
				let numberOfReturnsArray = hasNumberOfReturnsFilter && geometry.attributes.numberOfReturns
					? geometry.attributes.numberOfReturns.array : null;
				let gpsTimeArray = hasGPSTimeFilter && geometry.attributes.gpsTime
					? geometry.attributes.gpsTime.array : null;
				let pointSourceIDArray = hasPointSourceIDFilter && geometry.attributes.pointSourceID
					? geometry.attributes.pointSourceID.array : null;

				for (let i = 0; i < numPoints; i++) {
					if (classificationArray) {
						let classId = classificationArray[i];
						let classEntry = this.viewer.classifications[classId];
						if (classEntry && classEntry.visible === false) { continue; }
					}

					if (intensityArray) {
						let val = intensityArray[i];
						if (val < intensityRange[0] || val > intensityRange[1]) { continue; }
					}

					if (returnNumberArray) {
						let val = returnNumberArray[i];
						if (val < returnNumberRange[0] || val > returnNumberRange[1]) { continue; }
					}

					if (numberOfReturnsArray) {
						let val = numberOfReturnsArray[i];
						if (val < numberOfReturnsRange[0] || val > numberOfReturnsRange[1]) { continue; }
					}

					if (gpsTimeArray) {
						let val = gpsTimeArray[i];
						if (val < gpsTimeRange[0] || val > gpsTimeRange[1]) { continue; }
					}

					if (pointSourceIDArray) {
						let val = pointSourceIDArray[i];
						if (val < pointSourceIDRange[0] || val > pointSourceIDRange[1]) { continue; }
					}

					let lx = positions[i * 3 + 0];
					let ly = positions[i * 3 + 1];
					let lz = positions[i * 3 + 2];

					let e = nodeWorld.elements;
					let wx = e[0] * lx + e[4] * ly + e[8] * lz + e[12];
					let wy = e[1] * lx + e[5] * ly + e[9] * lz + e[13];
					let wz = e[2] * lx + e[6] * ly + e[10] * lz + e[14];

					if (hasZFilter && (wz < zRange[0] || wz > zRange[1])) { continue; }

					// Project onto transect axis (perpendicular to curb)
					let toX = wx - transectStart.x;
					let toY = wy - transectStart.y;

					let along = toX * transectDirX + toY * transectDirY;
					if (along < 0 || along > transectLength) { continue; }

					// Perpendicular distance (along curb direction)
					let perp = Math.abs(toX * curbDirX + toY * curbDirY);
					if (perp > halfSwath) { continue; }

					collectedPoints.push({ distanceAlong: along, elevation: wz });
				}
			}
		}

		console.log("CurbReveal: collected " + collectedPoints.length +
			" points, transect length = " + transectLength.toFixed(3) +
			"m, swath = " + (halfSwath * 2).toFixed(3) + "m");

		if (collectedPoints.length < 20) {
			this.showWarningOverlay(curbReveal, "Insufficient points — zoom in or widen the view");
			return;
		}

		// --- Stage 2: Fine-bin and smooth ---
		let binnedPoints = this.fineBinMedian(collectedPoints, transectLength);

		if (binnedPoints.length < 5) {
			this.showWarningOverlay(curbReveal, "Too few data points after filtering");
			return;
		}

		// Smooth for edge detection (uniform Gaussian)
		let smoothedForDetection = this.smoothGaussian(binnedPoints, 0.10);

		curbReveal.profilePoints = binnedPoints;

		// --- Stage 3: Detect curb edge ---
		// The user's line is at the transect midpoint — bias detection toward it
		let edge = this.detectCurbEdge(smoothedForDetection, transectLength / 2);

		if (!edge) {
			this.showWarningOverlay(curbReveal, "No clear curb edge detected — try a different location");
			return;
		}

		console.log("CurbReveal: detected edge at " + edge.edgeDistanceAlong.toFixed(3) +
			"m, gradient=" + edge.maxGradient.toFixed(3) +
			", reveal=" + (edge.revealMeters * 39.3701).toFixed(1) + "in");

		curbReveal.detectedEdge = edge;

		// Segment-aware smoothing for display: smooth each surface independently,
		// insert a right-angle step at the curb edge for a crisp drop-off.
		curbReveal.profileSmoothed = this.smoothSegmented(
			binnedPoints, edge.topDist, edge.bottomDist,
			edge.curbElev, edge.roadElev, 0.10
		);

		// --- Stage 4: Synthesize curb/road lines ---
		this.synthesizeLines(curbReveal, edge, transectStart,
			transectDirX, transectDirY, curbDirX, curbDirY, curbLength);

		// --- Stage 5: Populate compatibility fields ---
		let revealInches = edge.revealMeters * 39.3701;
		curbReveal.revealData = [{
			distance: 0,
			revealMeters: edge.revealMeters,
			revealInches: revealInches
		}];
		curbReveal.revealRange = { min: edge.revealMeters, max: edge.revealMeters };
		curbReveal.numBins = 1;

		// Uniform 256-sample profile for GPU coloring (single reveal value)
		let profile = new Float32Array(256);
		profile.fill(0.5);
		curbReveal.revealProfile = profile;

		curbReveal.initialized = true;

		let vpMatrix = new THREE.Matrix4().multiplyMatrices(
			curbReveal.projMatrix, curbReveal.viewMatrix
		);
		curbReveal.ndcVertices = curbReveal.quadCorners.map(corner => {
			let v = corner.clone().applyMatrix4(vpMatrix);
			return { x: v.x / v.w, y: v.y / v.w };
		});

		// --- Stage 6: Visualize ---
		this.addRevealLines(curbReveal);
		this.addVisualizationLines(curbReveal, transectStart, transectDirX, transectDirY, transectLength);
		if (this.viewer.curbRevealWindow) {
			this.viewer.curbRevealWindow.show(curbReveal);
		}
	}

	// Detect the curb edge from a smoothed elevation profile.
	// userDist: the distance along the transect where the user's curb line is
	// (typically the midpoint). Edges near this position are strongly preferred.
	detectCurbEdge(smoothedPoints, userDist) {
		if (smoothedPoints.length < 3) { return null; }

		let gradients = [];
		for (let i = 1; i < smoothedPoints.length; i++) {
			let dZ = smoothedPoints[i].elevation - smoothedPoints[i - 1].elevation;
			let dDist = smoothedPoints[i].distanceAlong - smoothedPoints[i - 1].distanceAlong;
			if (dDist <= 0) { continue; }

			gradients.push({
				midDist: (smoothedPoints[i].distanceAlong + smoothedPoints[i - 1].distanceAlong) / 2,
				gradient: dZ / dDist,
				index: i
			});
		}

		if (gradients.length === 0) { return null; }

		// Find peak gradient near the user's line position.
		// Hard clamp: only consider edges within 0.4m of the user's line.
		// Soft preference: Gaussian falloff (sigma=0.2m) within that range.
		let maxDist = 0.1524; // 0.5 feet — hard cutoff
		let proxSigma = 0.2;
		let invProx2 = -0.5 / (proxSigma * proxSigma);

		let peakIdx = -1;
		let peakAbsGrad = 0;
		let bestScore = 0;

		for (let i = 0; i < gradients.length; i++) {
			let distFromUser = Math.abs(gradients[i].midDist - userDist);
			if (distFromUser > maxDist) { continue; }

			let absGrad = Math.abs(gradients[i].gradient);
			let proximityWeight = Math.exp(distFromUser * distFromUser * invProx2);
			let score = absGrad * proximityWeight;

			if (score > bestScore) {
				bestScore = score;
				peakIdx = i;
				peakAbsGrad = absGrad;
			}
		}

		if (peakIdx === -1) { return null; }

		if (peakAbsGrad < 0.02) { return null; }

		let peakDist = gradients[peakIdx].midDist;
		let threshold = peakAbsGrad * 0.5;

		// Walk outward from peak to find edge boundaries, but clamp
		// to maxDist from the user's line so lines stay close.
		let topIdx = peakIdx;
		let bottomIdx = peakIdx;

		for (let i = peakIdx - 1; i >= 0; i--) {
			if (Math.abs(gradients[i].midDist - userDist) > maxDist) { break; }
			if (Math.abs(gradients[i].gradient) < threshold) { break; }
			topIdx = i;
		}

		for (let i = peakIdx + 1; i < gradients.length; i++) {
			if (Math.abs(gradients[i].midDist - userDist) > maxDist) { break; }
			if (Math.abs(gradients[i].gradient) < threshold) { break; }
			bottomIdx = i;
		}

		let topDist = gradients[topIdx].midDist;
		let bottomDist = gradients[bottomIdx].midDist;

		if (topDist > bottomDist) {
			[topDist, bottomDist] = [bottomDist, topDist];
		}

		let highSideElev = this.getStableElevation(smoothedPoints, topDist, -1);
		let lowSideElev = this.getStableElevation(smoothedPoints, bottomDist, +1);

		if (highSideElev === null || lowSideElev === null) { return null; }

		let curbElev, roadElev;
		if (highSideElev >= lowSideElev) {
			curbElev = highSideElev;
			roadElev = lowSideElev;
		} else {
			curbElev = lowSideElev;
			roadElev = highSideElev;
		}

		return {
			edgeDistanceAlong: peakDist,
			topDist: topDist,
			bottomDist: bottomDist,
			curbElev: curbElev,
			roadElev: roadElev,
			revealMeters: curbElev - roadElev,
			maxGradient: peakAbsGrad
		};
	}

	// Walk outward from startDist until elevation flattens, then average there.
	getStableElevation(points, startDist, direction) {
		let candidates = [];
		for (let p of points) {
			let offset = (p.distanceAlong - startDist) * direction;
			if (offset > 0.03) {
				candidates.push(p);
			}
		}

		candidates.sort((a, b) => {
			return Math.abs(a.distanceAlong - startDist) - Math.abs(b.distanceAlong - startDist);
		});

		if (candidates.length < 3) {
			if (candidates.length === 0) { return null; }
			let sum = 0;
			for (let p of candidates) { sum += p.elevation; }
			return sum / candidates.length;
		}

		let windowSize = Math.min(5, Math.max(3, Math.floor(candidates.length / 3)));
		let flatThreshold = 0.02;
		let bestWindowStart = -1;

		for (let i = 0; i <= candidates.length - windowSize; i++) {
			let first = candidates[i];
			let last = candidates[i + windowSize - 1];
			let dDist = Math.abs(last.distanceAlong - first.distanceAlong);
			if (dDist < 0.001) { continue; }

			let dElev = last.elevation - first.elevation;
			let grad = Math.abs(dElev / dDist);

			if (grad < flatThreshold) {
				bestWindowStart = i;
				break;
			}
		}

		if (bestWindowStart === -1) {
			let startIdx = Math.max(0, candidates.length - windowSize);
			let sum = 0;
			let count = 0;
			for (let i = startIdx; i < candidates.length; i++) {
				sum += candidates[i].elevation;
				count++;
			}
			return count > 0 ? sum / count : null;
		}

		let sum = 0;
		for (let i = bestWindowStart; i < bestWindowStart + windowSize; i++) {
			sum += candidates[i].elevation;
		}
		return sum / windowSize;
	}

	// Place curb/road lines along the user's curb direction at the
	// detected edge positions on the perpendicular transect.
	synthesizeLines(curbReveal, edge, transectStart,
		transectDirX, transectDirY, curbDirX, curbDirY, curbLength) {

		// Line half-length = half the user's curb line length
		let lineHalfLength = curbLength / 2;

		// Determine which side is curb (higher) vs road (lower)
		let elevAtTop = this.getStableElevation(curbReveal.profileSmoothed, edge.topDist, -1);
		let elevAtBottom = this.getStableElevation(curbReveal.profileSmoothed, edge.bottomDist, +1);

		let curbDist = edge.topDist;
		let roadDist = edge.bottomDist;
		let curbZ = edge.curbElev;
		let roadZ = edge.roadElev;

		if (elevAtTop !== null && elevAtBottom !== null && elevAtBottom > elevAtTop) {
			curbDist = edge.bottomDist;
			roadDist = edge.topDist;
		}

		// World positions along the perpendicular transect
		let curbCenter = new THREE.Vector3(
			transectStart.x + transectDirX * curbDist,
			transectStart.y + transectDirY * curbDist,
			curbZ
		);
		let roadCenter = new THREE.Vector3(
			transectStart.x + transectDirX * roadDist,
			transectStart.y + transectDirY * roadDist,
			roadZ
		);

		// Curb line: runs along user's curb direction
		curbReveal.curbLine.start = new THREE.Vector3(
			curbCenter.x + curbDirX * lineHalfLength,
			curbCenter.y + curbDirY * lineHalfLength,
			curbZ
		);
		curbReveal.curbLine.end = new THREE.Vector3(
			curbCenter.x - curbDirX * lineHalfLength,
			curbCenter.y - curbDirY * lineHalfLength,
			curbZ
		);

		// Road line: parallel to curb, offset perpendicular
		curbReveal.roadLine.start = new THREE.Vector3(
			roadCenter.x + curbDirX * lineHalfLength,
			roadCenter.y + curbDirY * lineHalfLength,
			roadZ
		);
		curbReveal.roadLine.end = new THREE.Vector3(
			roadCenter.x - curbDirX * lineHalfLength,
			roadCenter.y - curbDirY * lineHalfLength,
			roadZ
		);

		curbReveal.quadCorners = [
			curbReveal.curbLine.start.clone(),
			curbReveal.curbLine.end.clone(),
			curbReveal.roadLine.end.clone(),
			curbReveal.roadLine.start.clone()
		];
	}

	// Fine-bin points into 2.5cm bins, taking median per bin.
	fineBinMedian(points, transectLength) {
		let binSize = 0.025;
		let numBins = Math.max(20, Math.ceil(transectLength / binSize));
		let bins = new Array(numBins);
		for (let i = 0; i < numBins; i++) { bins[i] = []; }

		for (let p of points) {
			let idx = Math.floor(p.distanceAlong / transectLength * numBins);
			if (idx >= numBins) { idx = numBins - 1; }
			if (idx < 0) { idx = 0; }
			bins[idx].push(p.elevation);
		}

		let result = [];
		for (let b = 0; b < numBins; b++) {
			if (bins[b].length === 0) { continue; }
			bins[b].sort((a, c) => a - c);
			let median = this.percentile(bins[b], 0.5);
			let distance = (b + 0.5) / numBins * transectLength;
			result.push({ distanceAlong: distance, elevation: median });
		}

		return result;
	}

	// Gaussian spatial smoothing.
	smoothGaussian(points, sigma) {
		if (points.length < 3) { return points.slice(); }

		let cutoff = sigma * 3;
		let invS2 = -0.5 / (sigma * sigma);
		let smoothed = [];

		for (let i = 0; i < points.length; i++) {
			let centerDist = points[i].distanceAlong;
			let weightSum = 0;
			let valueSum = 0;

			for (let j = i; j >= 0; j--) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist < -cutoff) { break; }
				let w = Math.exp(dDist * dDist * invS2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			for (let j = i + 1; j < points.length; j++) {
				let dDist = points[j].distanceAlong - centerDist;
				if (dDist > cutoff) { break; }
				let w = Math.exp(dDist * dDist * invS2);
				weightSum += w;
				valueSum += w * points[j].elevation;
			}

			smoothed.push({
				distanceAlong: centerDist,
				elevation: valueSum / weightSum,
			});
		}

		return smoothed;
	}

	// Segment-aware smoothing: split profile at curb zone, smooth each flat
	// surface independently, insert a right-angle step at the curb edge.
	smoothSegmented(binnedPoints, topDist, bottomDist, curbElev, roadElev, sigma) {
		let epsilon = 0.005; // ~0.2 inches, tiny gap for vertical step rendering

		// Split points into before-zone and after-zone segments
		let beforeSeg = [];
		let afterSeg = [];

		for (let p of binnedPoints) {
			if (p.distanceAlong < topDist) {
				beforeSeg.push(p);
			} else if (p.distanceAlong > bottomDist) {
				afterSeg.push(p);
			}
			// Points inside the curb zone (topDist..bottomDist) are excluded
		}

		// Gaussian-smooth each segment independently
		let smoothedBefore = this.smoothGaussian(beforeSeg, sigma);
		let smoothedAfter = this.smoothGaussian(afterSeg, sigma);

		// Determine which side is higher (curb) and which is lower (road)
		let beforeElev = smoothedBefore.length > 0
			? smoothedBefore[smoothedBefore.length - 1].elevation
			: curbElev;
		let afterElev = smoothedAfter.length > 0
			? smoothedAfter[0].elevation
			: roadElev;

		// Build result: before segment, right-angle step, after segment
		let result = [];

		for (let p of smoothedBefore) {
			result.push(p);
		}

		// Insert vertical step at the edge midpoint
		let edgeMid = (topDist + bottomDist) / 2;
		result.push({ distanceAlong: edgeMid, elevation: beforeElev });
		result.push({ distanceAlong: edgeMid, elevation: afterElev });

		for (let p of smoothedAfter) {
			result.push(p);
		}

		// Sort by distance to ensure proper ordering
		result.sort((a, b) => a.distanceAlong - b.distanceAlong);

		return result;
	}

	// 3D lines for curb (red) and road (blue), using centroid-offset pattern.
	addRevealLines(curbReveal) {
		let curbLine = curbReveal.curbLine;
		let roadLine = curbReveal.roadLine;

		let curbDelta = curbLine.end.clone().sub(curbLine.start);
		let curbGeom = new THREE.BufferGeometry().setFromPoints([
			new THREE.Vector3(0, 0, 0), curbDelta
		]);
		let curbMat = new THREE.LineBasicMaterial({
			color: 0xcc0000, depthTest: false, depthWrite: false
		});
		let curbLineObj = new THREE.Line(curbGeom, curbMat);
		curbLineObj.position.copy(curbLine.start);
		this.toolScene.add(curbLineObj);
		curbReveal._lineObjects = curbReveal._lineObjects || [];
		curbReveal._lineObjects.push(curbLineObj);

		let roadDelta = roadLine.end.clone().sub(roadLine.start);
		let roadGeom = new THREE.BufferGeometry().setFromPoints([
			new THREE.Vector3(0, 0, 0), roadDelta
		]);
		let roadMat = new THREE.LineBasicMaterial({
			color: 0x0066cc, depthTest: false, depthWrite: false
		});
		let roadLineObj = new THREE.Line(roadGeom, roadMat);
		roadLineObj.position.copy(roadLine.start);
		this.toolScene.add(roadLineObj);
		curbReveal._lineObjects.push(roadLineObj);
	}

	// Draw the user's curb line (green) and the auto-generated transect (yellow dashed)
	addVisualizationLines(curbReveal, transectStart, transectDirX, transectDirY, transectLength) {
		let userStart = curbReveal.userLine.start;
		let userEnd = curbReveal.userLine.end;

		// User's curb line (green solid)
		let userDelta = new THREE.Vector3(
			userEnd.x - userStart.x, userEnd.y - userStart.y, userEnd.z - userStart.z
		);
		let userGeom = new THREE.BufferGeometry().setFromPoints([
			new THREE.Vector3(0, 0, 0), userDelta
		]);
		let userMat = new THREE.LineBasicMaterial({
			color: 0x00cc66, depthTest: false, depthWrite: false
		});
		let userLineObj = new THREE.Line(userGeom, userMat);
		userLineObj.position.copy(userStart);
		this.toolScene.add(userLineObj);
		curbReveal._lineObjects = curbReveal._lineObjects || [];
		curbReveal._lineObjects.push(userLineObj);

		// Auto-generated transect (yellow dashed)
		let transectEnd = new THREE.Vector3(
			transectStart.x + transectDirX * transectLength,
			transectStart.y + transectDirY * transectLength,
			transectStart.z
		);
		let transectDelta = transectEnd.clone().sub(transectStart);
		let transectGeom = new THREE.BufferGeometry().setFromPoints([
			new THREE.Vector3(0, 0, 0), transectDelta
		]);
		let transectMat = new THREE.LineDashedMaterial({
			color: 0xcccc00, depthTest: false, depthWrite: false,
			dashSize: 0.10, gapSize: 0.08
		});
		let transectLineObj = new THREE.Line(transectGeom, transectMat);
		transectLineObj.position.copy(transectStart);
		transectLineObj.computeLineDistances();
		this.toolScene.add(transectLineObj);
		curbReveal._lineObjects.push(transectLineObj);

		// Edge marker at detected edge
		if (curbReveal.detectedEdge) {
			let edge = curbReveal.detectedEdge;
			let edgePos = new THREE.Vector3(
				transectStart.x + transectDirX * edge.edgeDistanceAlong,
				transectStart.y + transectDirY * edge.edgeDistanceAlong,
				(edge.curbElev + edge.roadElev) / 2
			);
			let sphereGeom = new THREE.SphereGeometry(0.03, 12, 8);
			let sphereMat = new THREE.MeshBasicMaterial({
				color: 0xffff00, depthTest: false, depthWrite: false
			});
			let sphereObj = new THREE.Mesh(sphereGeom, sphereMat);
			sphereObj.position.copy(edgePos);
			this.toolScene.add(sphereObj);
			curbReveal._lineObjects.push(sphereObj);
		}
	}

	showWarningOverlay(curbReveal, message) {
		let panel = document.createElement("div");
		panel.className = "curb-reveal-metrics";
		panel.style.cssText = [
			"position: absolute",
			"top: 56px",
			"right: 10px",
			"background: var(--card, #fff)",
			"border: 1px solid var(--destructive, #FF470A)",
			"border-radius: 10px",
			"padding: 12px 16px",
			"color: var(--foreground, #0D0D0C)",
			"font-family: 'Geist', Arial, sans-serif",
			"font-size: 13px",
			"z-index: 200",
			"pointer-events: auto",
			"min-width: 180px",
			"box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
		].join("; ");

		panel.innerHTML = `
			<div style="font-weight: 600; margin-bottom: 8px; color: var(--destructive, #FF470A); font-size: 14px; display: flex; align-items: center;">
				Curb Reveal
				<span style="flex-grow: 1;"></span>
				<span style="cursor: pointer; color: var(--muted-foreground, #505A56); font-size: 16px; line-height: 1;" class="cr-close">&times;</span>
			</div>
			<div>${message}</div>
		`;

		let domElement = this.viewer.renderer.domElement;
		domElement.parentElement.appendChild(panel);
		curbReveal.metricsPanel = panel;

		panel.querySelector(".cr-close").addEventListener("click", () => {
			panel.remove();
			curbReveal.metricsPanel = null;
		});
	}

	percentile(sorted, p) {
		let n = sorted.length;
		let index = (n - 1) * p;
		let lower = Math.floor(index);
		let upper = Math.ceil(index);
		let weight = index - lower;

		if (upper >= n) { return sorted[n - 1]; }
		return sorted[lower] * (1 - weight) + sorted[upper] * weight;
	}

	update() {
		let camera = this.viewer.scene.getActiveCamera();
		this.light.position.copy(camera.position);
	}

	render() {
		this.viewer.renderer.render(this.toolScene, this.viewer.scene.getActiveCamera());
	}
}
